import { WsmErrorCode, WsmErrorFragment } from "@dashboard/graphql";
import commonErrorMessages, {
  getCommonFormFieldErrorMessage,
} from "@dashboard/utils/errors/common";
import { defineMessages, IntlShape } from "react-intl";

// The fitment members of WsmErrorCode. The backend sends its own merchant
// sentence in `message` and the Dashboard renders that verbatim; these strings
// are the fallback for a payload that carries a code and no sentence.
export const wsmFitmentErrorMessages = defineMessages({
  inUse: {
    defaultMessage: "Still in use: something else points at this, so it cannot be deleted",
    id: "upbcp9",
    description: "fitment, IN_USE error",
  },
  unique: {
    defaultMessage: "That name is already taken",
    id: "RGmrAx",
    description: "fitment, UNIQUE error",
  },
  valueNotInSchema: {
    defaultMessage: "That value belongs to an axis this schema does not have",
    id: "TKSE4S",
    description: "fitment, VALUE_NOT_IN_SCHEMA error",
  },
  duplicateAxisInApplication: {
    defaultMessage: "An application takes at most one value per axis",
    id: "VKyfEL",
    description: "fitment, DUPLICATE_AXIS_IN_APPLICATION error",
  },
  duplicateQualifierInApplication: {
    defaultMessage: "An application takes at most one value per qualifier",
    id: "eCwJLZ",
    description: "fitment, DUPLICATE_QUALIFIER_IN_APPLICATION error",
  },
  duplicateAxisSet: {
    defaultMessage: "A schema with exactly these axes already exists",
    id: "qyymJq",
    description: "fitment, DUPLICATE_AXIS_SET error",
  },
  duplicatedInputItem: {
    defaultMessage: "The same row is listed twice",
    id: "dHqAKZ",
    description: "fitment, DUPLICATED_INPUT_ITEM error",
  },
  notFound: {
    defaultMessage:
      "That no longer exists; it may have been deleted. Reload the page and try again",
    id: "TQvHY5",
    description: "fitment, NOT_FOUND error",
  },
  bulkLimit: {
    defaultMessage: "Too many rows in one go; add at most 500 at a time",
    id: "bFlsaz",
    description: "fitment, BULK_LIMIT error",
  },
});

const codeToMessage = {
  [WsmErrorCode.IN_USE]: wsmFitmentErrorMessages.inUse,
  [WsmErrorCode.UNIQUE]: wsmFitmentErrorMessages.unique,
  [WsmErrorCode.VALUE_NOT_IN_SCHEMA]: wsmFitmentErrorMessages.valueNotInSchema,
  [WsmErrorCode.DUPLICATE_AXIS_IN_APPLICATION]: wsmFitmentErrorMessages.duplicateAxisInApplication,
  [WsmErrorCode.DUPLICATE_QUALIFIER_IN_APPLICATION]:
    wsmFitmentErrorMessages.duplicateQualifierInApplication,
  [WsmErrorCode.DUPLICATE_AXIS_SET]: wsmFitmentErrorMessages.duplicateAxisSet,
  [WsmErrorCode.DUPLICATED_INPUT_ITEM]: wsmFitmentErrorMessages.duplicatedInputItem,
  [WsmErrorCode.BULK_LIMIT]: wsmFitmentErrorMessages.bulkLimit,
  [WsmErrorCode.NOT_FOUND]: wsmFitmentErrorMessages.notFound,
} as Partial<Record<WsmErrorCode, (typeof wsmFitmentErrorMessages)["inUse"]>>;

/**
 * The model's own sentence first, this module's wording second, the Dashboard's
 * common wording last. Returns undefined for no error, so a helperText prop can
 * take the result directly.
 */
export const getWsmFitmentErrorMessage = (
  error: WsmErrorFragment | undefined,
  intl: IntlShape,
): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (error.message) {
    return error.message;
  }

  const message = error.code ? codeToMessage[error.code] : undefined;

  if (message) {
    return intl.formatMessage(message);
  }

  return getCommonFormFieldErrorMessage({ ...error, code: error.code }, intl);
};

export const wsmFitmentUnknownErrorMessage = commonErrorMessages.unknownError;

/** The error on a field, or on any row of it: `values.3` lands on the values field. */
export const getWsmFitmentFieldError = (errors: WsmErrorFragment[], field: string) =>
  errors.find(error => error.field === field || error.field?.startsWith(`${field}.`));

/** Every error that is not tied to a field the screen shows: rendered as one line. */
export const getWsmFitmentGeneralErrors = (errors: WsmErrorFragment[], fields: string[]) =>
  errors.filter(error => !fields.some(field => getWsmFitmentFieldError([error], field)));
